import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { H6 } from 'foundations-components/transitional/components/H6';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import styled from 'foundations-theming/utils';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { trellisBreakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { useState } from 'react';
import { PROFESSIONAL } from 'self-service-api/constants/ProductLevels';
import { SALES } from 'self-service-api/constants/ProductLines';
import { BUY_TOUCHLESS, TALK_TO_SALES, TRIAL } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import { SCREENS } from '../../constants/tracking/Screens';
import { APP, SSM0010_RECOMMENDED_SALES_HEADER_BANNER } from '../../constants/tracking/TrackingSources';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useAppSelector } from '../../reducers';
import { productSelector, upgradeProductSelector } from '../../selectors/ProductsSelectors';
import { purchaseMotionsSelector } from '../../selectors/PurchaseMotionSelectors';
import { trackHoverInteraction, trackVideoInteraction } from '../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { LoadableWrapper } from '../common/LoadableWrapper';
import DetailedTermSelector from '../common/TermSelectors/DetailedTermSelector';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import LoadingMerchCardCTAs from '../merch-card/LoadingMerchCardCTAs';
import { FullPriceContainerBlockLarge } from '../merch-card/price-display/PriceStyledComponents';
import { useShowPricePerMonthPerSeat } from '../merch-card/seats-display/useSeatsDisplay';
import { useSkuAdditionalUnits } from '../merch-card/useSkuAdditionalUnits';
import RecommendedSalesHubProCTAsContainer from './RecommendedSalesHubProCTAsContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopyContainer = styled.div.withConfig({
  displayName: "RecommendedSalesHeaderBanner__CopyContainer",
  componentId: "srq2gt-0"
})({
  inlineSize: '45%',
  [trellisBreakpoint.tabletPortraitLarge]: {
    inlineSize: '100%'
  }
});
const VideoContainer = styled.div.withConfig({
  displayName: "RecommendedSalesHeaderBanner__VideoContainer",
  componentId: "srq2gt-1"
})(({
  theme
}) => {
  return {
    inlineSize: '50%',
    marginBlock: 'auto',
    marginInlineStart: theme.space[100],
    boxShadow: theme.boxShadow[300],
    borderRadius: theme.borderRadius[100],
    [trellisBreakpoint.tabletPortraitLarge]: {
      display: 'none'
    }
  };
});
const ButtonsContainer = styled.div.withConfig({
  displayName: "RecommendedSalesHeaderBanner__ButtonsContainer",
  componentId: "srq2gt-2"
})({
  inlineSize: '70%',
  [trellisBreakpoint.tabletPortraitLarge]: {
    inlineSize: '100%'
  }
});
const RecommendedSalesHeaderBanner = () => {
  const isAppReady = useGlobalLoading();
  const SSM0010_RECOMMENDED_SALES_VIDEO_NAME = 'SSM0010_recommended_sales_header_video';
  const [progress, setProgress] = useState(0);
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const purchaseMotionsButton = [BUY_TOUCHLESS]; // fixed button

  const product = useAppSelector(state => {
    return productSelector(state, {
      productLine: SALES,
      productLevel: PROFESSIONAL
    });
  });
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel: PROFESSIONAL,
    productLine: SALES
  });
  const purchaseMotionsText = useAppSelector(state => {
    if (!isAppReady) return [];
    const motions = purchaseMotionsSelector(state, {
      skuId: skuId || 0
    });
    return motions.includes(TRIAL) ? [TRIAL, TALK_TO_SALES] : [TALK_TO_SALES];
  });
  const upgradeProduct = useAppSelector(state => upgradeProductSelector(state, {
    skuIds: skuId ? [skuId] : []
  }));
  const apiName = product && product.apiName;
  const {
    purchaseOptionsForTerm,
    loading: purchaseOptionsLoading
  } = usePurchaseableProductsForTerm({
    queryParamTerm,
    apiName
  });
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeat({
    skuId
  });
  if (!product) return null;
  const loading = purchaseOptionsLoading || !isAppReady;
  const productConfigurations = [createProductConfig({
    skuId: product === null || product === void 0 ? void 0 : product.currentSkuId,
    quantity: 1
  })];
  const upgradeData = {
    app: APP,
    screen: SCREENS.RECOMMENDED,
    uniqueId: `${SSM0010_RECOMMENDED_SALES_HEADER_BANNER}-${upgradeProduct}`,
    upgradeProduct
  };
  const productConfigurationsToPass = (isAdditionalUnitSkuIdAQuantityPack && additionalUnitQuantity > 0 ? [skuId, additionalUnitSkuId] : [skuId]).filter(id => id !== undefined).map(mapSkuId => createProductConfig({
    skuId: mapSkuId
  }));
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-primary-subtle",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      paddingBlock: "1100",
      paddingInline: "1000",
      maxInlineSize: "1200px",
      marginInline: "auto",
      children: [/*#__PURE__*/_jsxs(CopyContainer, {
        children: [/*#__PURE__*/_jsxs(Badge, {
          use: "oz",
          marginBlockEnd: "500",
          display: "block",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.recommendedForYou"
          }), ' ', /*#__PURE__*/_jsx(HelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.ssm0010Recommended.recommendedForYouTooltip"
            }),
            marginInlineStart: "100",
            use: "on-dark",
            tooltipPlacement: "right",
            tooltipProps: {
              use: 'longform',
              onOpenChange: e => {
                if (e.currentTarget.value) {
                  trackHoverInteraction({
                    value: getI18nText(`pricingPage.pages.ssm0010Recommended.recommendedForYouTooltip`, {
                      locale: 'en-us'
                    }),
                    trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER
                  });
                }
              }
            }
          })]
        }), /*#__PURE__*/_jsx(H6, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.salesHubPro.upgradeTo"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "h1",
          typography: "body-700",
          marginBlockEnd: "500",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.salesHubPro.title"
          })
        }), /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: loading,
            heights: [16, 23, 16],
            widths: [60, 70, 130],
            children: /*#__PURE__*/_jsx(FullPriceContainerBlockLarge, {
              children: purchaseOptionsForTerm && /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
                pricingInfo: purchaseOptionsForTerm,
                showPricePerMonthPerSeat: showPricePerMonthPerSeat,
                seleniumId: apiName,
                currencyCode: currencyCode,
                currencyCodeUnit: currencyCodeUnit
              })
            })
          })
        }), /*#__PURE__*/_jsxs(ButtonsContainer, {
          children: [/*#__PURE__*/_jsx(View, {
            marginBlockStart: "500",
            as: "div",
            children: /*#__PURE__*/_jsx(DetailedTermSelector, {
              productConfigurations: productConfigurationsToPass,
              onChange: term => setQueryParamTerm(term),
              activeTerm: queryParamTerm,
              trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER
            })
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "500",
            as: "div",
            children: /*#__PURE__*/_jsxs(SkeletonLoadWrapper, {
              isLoading: !isAppReady,
              heights: [40],
              widths: [200],
              children: [/*#__PURE__*/_jsx(LoadableWrapper, {
                loader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui/components/auth-cta-motions/AuthedCTAs`),
                componentProps: Object.assign({
                  queryParamTerm,
                  upgradeData,
                  purchaseMotions: purchaseMotionsButton,
                  productConfigurations,
                  trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER,
                  primaryButtonUse: 'primary',
                  secondaryButtonUse: 'secondary'
                }, ctaVariantProps, {
                  purchaseMotionLocation: 'MERCH_CARD'
                }),
                LoadingComponent: LoadingMerchCardCTAs
              }), /*#__PURE__*/_jsx(Flex, {
                flexDirection: "row",
                alignItems: "start",
                marginBlockStart: "300",
                children: /*#__PURE__*/_jsx(RecommendedSalesHubProCTAsContainer, {
                  skuId: skuId,
                  additionalUnitQuantity: additionalUnitQuantity,
                  additionalUnitSkuId: additionalUnitSkuId,
                  productLine: SALES,
                  queryParamTerm: queryParamTerm,
                  purchaseMotions: purchaseMotionsText,
                  trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER,
                  purchaseMotionLocation: 'MERCH_CARD'
                })
              })]
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(VideoContainer, {
        "data-test-id": "recommended-sales-header-banner-video",
        children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
          videoId: 131206836656,
          onPlay: () => {
            trackVideoInteraction({
              action: 'play-video',
              name: SSM0010_RECOMMENDED_SALES_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER,
              videoTimestamp: progress
            });
          },
          onProgress: e => {
            setProgress(e.detail.currentTime);
          },
          onPause: () => {
            trackVideoInteraction({
              action: 'pause-video',
              name: SSM0010_RECOMMENDED_SALES_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER,
              videoTimestamp: progress
            });
          },
          onFinish: () => {
            trackVideoInteraction({
              action: 'finished-video',
              name: SSM0010_RECOMMENDED_SALES_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_SALES_HEADER_BANNER
            });
          }
        })
      })]
    })
  });
};
export default RecommendedSalesHeaderBanner;